import { useEffect, useRef, useState } from "react";
import { SkeletonDataTable } from "../../Loader/Loader";
import _ from "underscore";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTheme } from "../../../context/ThemeContext";
import { Menu } from "primereact/menu";
import { ListBox } from "primereact/listbox";
import { useCommonGrid } from "../../../Hooks/Common/useCommonGrid";

const TableRow = ({ row, columns, columnWidths, strippedTable = false, theme = "light", rowHeight = "30px", rowIndex, rowKey = null, level = 0, expandedState, setExpandedState, expandAll, fixedWidth, selected
}) => {

  let notSelected = {};
  Object.keys(selected?.data)?.forEach(key => {
    if (_.isEmpty(_.findWhere(selected?.data?.[key], { [key]: row?.[key] }))) {
      notSelected = row;
    }
  })

  if (!_.isEmpty(notSelected)) {
    return;
  }

  const cellStyle = (col) => ({
    left: col?.pinned === "left" ? 0 : "auto",
    right: col?.pinned === "right" ? 0 : "auto",
    height: rowHeight === "auto" ? "auto" : `${rowHeight}px`,
    paddingLeft: level > 0 && col?.toggleIcon ? `${level * 20}px` : "5px",
    ...col?.style,
    width: columnWidths[col?.key],
    minWidth: columnWidths[col?.key],
  });

  const hasChildren = row?.children && row?.children?.length > 0;
  const isExpanded = expandedState[rowKey || row?.id || rowIndex] !== undefined
    ? expandedState[rowKey || row?.id || rowIndex]
    : expandAll;

  const toggleRow = () => {
    if (hasChildren && setExpandedState) {
      setExpandedState(prev => ({
        ...prev,
        [rowKey || row?.id || rowIndex]: !isExpanded
      }));
    }
  };

  return (
    <>
      <div
        key={rowKey || rowIndex}
        className={`bid-col common-grid-row-hover${theme === "dark" ? "-dark" : ""} d-flex h-100 common-grid-row ${fixedWidth ? "fixed-width" : ""}`}
      >
        {columns
          ?.filter(col => !col?.hide)
          .map((col, colIndex) => {
            const cellClass = `common-grid-cell ${strippedTable ? "stripped" : ""} ${col?.pinned !== undefined ? "pinned" : ""} ${col?.pinned === "left" ? "pinned-left" : ""} ${col?.pinned === "right" ? "pinned-right" : ""}`;
            if (col?.toggleIcon) {
              return (
                <div key={col?.key || colIndex} className={cellClass} style={cellStyle(col)}>
                  <div className="w100 d-flex align-items-center gap-1">
                    <span className={`c-pointer common-grid-toggle-icon-container ${hasChildren ? "visible" : "invisible"}`} onClick={toggleRow} >
                      <FontAwesomeIcon icon={isExpanded ? "fa-solid fa-angle-down" : "fa-solid fa-angle-right"} />
                    </span>
                    <div>{col?.render ? col?.render(row) : row?.[col?.key]}</div>
                  </div>
                </div>
              );
            }
            return (
              <div key={col?.key || colIndex} className={cellClass} style={cellStyle(col)}>
                <div className="w100">
                  {(col?.render ? col?.render(row) : row?.[col?.key]) || ""}
                </div>
              </div>
            );
          })}
      </div>

      {/* Recursively render children */}
      {hasChildren && isExpanded && row?.children?.map((childRow, childIndex) => (
        <TableRow
          key={`child-${rowKey || rowIndex}-${childIndex}`}
          row={childRow}
          columns={columns}
          columnWidths={columnWidths}
          strippedTable={strippedTable}
          theme={theme}
          rowHeight={rowHeight}
          rowIndex={childIndex}
          rowKey={`child-${rowKey || rowIndex}-${childIndex}`}
          level={level + 1}
          expandedState={expandedState}
          setExpandedState={setExpandedState}
          expandAll={expandAll}
          selected={selected}
        />
      ))}
    </>
  );
};

const CommonGrid = ({ columns, data, loader, expandAll, strippedTable, bottomRowData, rowHeight, headerHeight, fixedWidth }) => {

  const { theme } = useTheme();
  const { expanded, columnWidths, sort, setSort, overlayRef, getOptions, selected, setSelected, getMenuItems, startResizing, setExpanded } = useCommonGrid({ columns, data, expandAll });

  return (
    <div className="w100 common-grid-container">
      {
        loader ? <SkeletonDataTable numColumns={6} numSkeletons={25} /> : <div className="common-grid-wrapper">
          {/* table headers */}
          <div className="common-grid-header-sticky">
            <div className="d-flex align-items-center">
              {columns?.map((col) => {
                const headerCellClass = `common-grid-header-cell ${strippedTable ? "stripped" : ""} ${col?.pinned !== undefined ? "pinned" : ""} ${col?.pinned === "left" ? "pinned-left" : ""} ${col?.pinned === "right" ? "pinned-right" : ""}`;
                return (
                  !col?.hide && <div key={col?.key} className={`fw-bold ${headerCellClass}`}
                    style={{
                      left: col?.pinned === "left" ? 0 : "auto",
                      right: col?.pinned === "right" ? 0 : "auto",
                      height: headerHeight ? `${headerHeight}px` : "30px",
                      ...col?.style, ...col?.headerStyle, width: columnWidths[col?.key], minWidth: columnWidths[col?.key]
                    }}>
                    <div className="d-flex align-items-center" style={{ width: col?.headerStyle?.width ? col?.headerStyle?.width : "100%", resize: "both", justifyContent: col?.headerStyle?.textAlign === "center" ? "center" : "between" }}>
                      {col?.checkbox ? col?.render({ id: "all" }, "all", data?.[0]?.children, data?.[0]?.category) :
                        <div className={`${((sort?.is_asc !== null && sort?.key === col?.key) || col?.filter) ? "w95 text-truncate" : ""} ${col?.sort !== false ? "c-pointer" : ""} ${col?.headerClass ?? ""}`} onClick={() => { if (col?.sort !== false) setSort((prev) => ({ ...prev, is_asc: prev?.is_asc === null ? true : prev?.is_asc ? false : null, key: (prev?.is_asc === null || prev?.is_asc) ? col?.key : "" })); }}>{col?.headerRender ? col?.headerRender() : col?.label}</div>
                      }
                      {(sort?.is_asc !== null && sort?.key === col?.key) && <FontAwesomeIcon icon={`fa-solid fa-arrow-${sort?.is_asc ? "up" : "down"}`} />}
                      {col?.filter && <span className="c-pointer" onClick={(event) => { overlayRef.current?.toggle(event); getOptions(col); setSelected((prev) => ({ ...prev, column: col })) }}><FontAwesomeIcon icon="fa-solid fa-filter" className="ms-2" /></span>}
                      <Menu ref={overlayRef} model={getMenuItems(selected?.column)} popup appendTo={document.body} className="survey-menu bucket-config" />
                      {/* Resize Handle */}
                      <div
                        onMouseDown={(e) => startResizing(e, col?.key)}
                        className="common-grid-resize-handle"
                      />

                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="common-grid-body">
            {
              data?.length === 0 ? (
                <div className="text-center text-muted">
                  <div>
                    <div className="text-center fw-semibold py-2 my-4">
                      No data found
                    </div>
                  </div>
                </div>
              ) : <>
                {/* table inner header */}
                {
                  data?.map((group) => (
                    <>
                      {
                        group?.title && <div className="c-pointer common-grid-group-header" key={group.id} onClick={() => setExpanded((prev) => ({ ...prev, [group.id]: !prev[group.id] }))}>
                          <div className={`py-1 common-grid-group-header-inner ${strippedTable ? "stripped" : ""}`}>
                            <div className="d-flex align-items-center fw-semibold">
                              <div className="me-1">{group.id && expanded[group.id] ? <FontAwesomeIcon icon="fa-solid fa-angle-down" /> : <FontAwesomeIcon icon="fa-solid fa-angle-right" />}</div>
                              {group?.titleTemplate ? group?.titleTemplate(group) : <div className="d-flex justify-content-between align-items-center gap-1" style={{ width: "98%" }}>
                                <span className="py-1 px-2 align-self-center fw-semibold my-1 common-grid-group-title">{group.title}</span>
                                <span className="px-2 align-self-center common-grid-group-count">{group.children.length} Item(s)</span>
                              </div>}
                            </div>
                          </div>
                        </div>
                      }

                      {/* table body */}
                      {group.id && expanded[group.id] &&
                        (
                          sort?.is_asc === null
                            ? group.children
                            : [...group.children].sort((a, b) => {
                              const aVal = a?.[sort?.key]?.toString() ?? "";
                              const bVal = b?.[sort?.key]?.toString() ?? "";
                              const isANumber = typeof aVal === "number" || (!isNaN(aVal) && aVal !== null && aVal !== "");
                              const isBNumber = typeof bVal === "number" || (!isNaN(bVal) && bVal !== null && bVal !== "");

                              let comparison = 0;

                              if (isANumber && isBNumber) {
                                comparison = Number(aVal) - Number(bVal);
                              } else {
                                comparison = aVal?.localeCompare(bVal);
                              }

                              return sort?.is_asc ? comparison : -comparison;
                            })
                        )?.map((row, idx) => (
                          <TableRow
                            row={row}
                            columns={columns}
                            columnWidths={columnWidths}
                            strippedTable={strippedTable}
                            theme={theme}
                            rowHeight={rowHeight}
                            rowKey={row?.id || idx}
                            expandedState={expanded}
                            setExpandedState={setExpanded}
                            expandAll={expandAll}
                            fixedWidth={fixedWidth}
                            selected={selected}
                          />

                        ))}
                    </>
                  ))
                }
                {/* table bottom pinned row */}
                {bottomRowData &&
                  <div className="bid-col d-flex common-grid-bottom-sticky">
                    {columns?.map((col) => {
                      const bottomCellClass = `common-grid-bottom-cell ${strippedTable ? "stripped" : ""} ${col?.pinned !== undefined ? "pinned" : ""} ${col?.pinned === "left" ? "pinned-left" : ""} ${col?.pinned === "right" ? "pinned-right" : ""}`;
                      return (
                        !col?.hide && <div key={col?.key} className={bottomCellClass}
                          style={{
                            left: col?.pinned === "left" ? 0 : "auto",
                            right: col?.pinned === "right" ? 0 : "auto",
                            width: columnWidths[col?.key],
                            minWidth: columnWidths[col?.key],
                            ...col?.style
                          }}>
                          {col?.render
                            ? col?.render(bottomRowData)
                            : bottomRowData[col?.key]}
                        </div>
                      );
                    })}
                  </div>}
              </>
            }
          </div>
        </div>
      }
    </div>

  );
};

export default CommonGrid

